import type { UserNotification } from '../api/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useNotificationsStore = defineStore('notifications', () => {
  // State
  const recentNotifications = ref<UserNotification[]>([])
  const isLoading = ref(false)
  const isLoadingMore = ref(false)
  const error = ref<string | null>(null)
  const hasMore = ref(true)
  const currentOffset = ref(0)
  const totalUnreadCount = ref(0)
  const lastFetchedAt = ref<number | null>(null)

  // Computed - use API count from first response
  const unreadCount = computed(() => totalUnreadCount.value)

  // Actions

  const fetchRecentNotifications = async (
    _reset = true,
    _forceRefresh = false,
  ): Promise<void> => {
    // Notifications feature is disabled
    recentNotifications.value = []
    hasMore.value = false
  }

  const loadMoreNotifications = async (): Promise<void> => {
    if (!hasMore.value || isLoadingMore.value || isLoading.value) 
      return
    await fetchRecentNotifications(false)
  }

  const refreshUnreadCount = async (): Promise<void> => {
    // Notifications feature is disabled
    totalUnreadCount.value = 0
  }

  const invalidateCache = (): void => {
    lastFetchedAt.value = null
  }

  const updateNotificationReadStatus = (notificationId: number): void => {
    const notification = recentNotifications.value.find(
      n => n.id === notificationId,
    )
    if (notification && !notification.is_read) {
      notification.is_read = true
      // Decrement unread count
      if (totalUnreadCount.value > 0) {
        totalUnreadCount.value--
      }
    }
  }

  const markAllAsRead = async (): Promise<void> => {
    try {
      const { markNotificationAsReadRequest } = await import(
        '../api/notificationsApi',
      )
      await markNotificationAsReadRequest({ notification_ids: [] })
      // Update all notifications to read
      recentNotifications.value.forEach((n) => {
        n.is_read = true
      })
      // Reset unread count
      totalUnreadCount.value = 0
    }
    catch (err) {
      console.error('Error marking all as read:', err)
      throw err
    }
  }

  const clearNotifications = (): void => {
    recentNotifications.value = []
    error.value = null
    currentOffset.value = 0
    hasMore.value = true
    totalUnreadCount.value = 0
    lastFetchedAt.value = null
  }

  return {
    // State
    recentNotifications,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    // Computed
    unreadCount,
    // Actions
    fetchRecentNotifications,
    loadMoreNotifications,
    updateNotificationReadStatus,
    markAllAsRead,
    refreshUnreadCount,
    invalidateCache,
    clearNotifications,
  }
})
